import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { provideClientHydration, withNoIncrementalHydration } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  PRIMARY_OUTLET,
  provideRouter,
  withComponentInputBinding,
  withExperimentalAutoCleanupInjectors,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';
import { ApiModule, Configuration, ConfigurationParameters } from '@elite/api-client';

import { provideServiceWorker } from '@angular/service-worker';
import {
  AuthenticationEffects,
  authenticationFeatureKey,
  AUTHORIZATION_FEATURE_KEY,
  AuthorizationEffects,
  authorizationReducer,
  reducer as authReducer,
} from '@elite/authentication/domain';
import { CarDomainProviders } from '@elite/car/api';
import { authInterceptor, CustomErrorHandler, CustomSerializer, metaReducers } from '@elite/core';
import { loadingInterceptor } from '@elite/layout';
import { provideProblemToastLifecycle } from '@elite/shared/util-http-response';
import { OPERATION_AREAS_FEATURE_KEY, OperationAreasEffects, operationAreasReducer } from '@elite/operation-areas/api';
import { provideRealtimeDomain, SUPPORT_CALL_MEDIA } from '@elite/realtime/api';
import { SupportCallsService } from './calls/support-calls.service';
import { provideRideAlertDomain } from '@elite/ride/api';
import { provideTicketDomain } from '@elite/ticket/domain';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { APP_LOGO_URL, APP_NAME, APP_VERSION, SUPPORT_CALLS_ENABLED } from '@elite/layout';
import { TENANT_ID } from '@elite/authentication/domain';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { AppUpdateService } from './app-update.service';
import { APP_VERSION_BUILD } from '../version';

/**
 * The path of the primary outlet, ignoring auxiliary outlets, query params and matrix params.
 *
 * View transitions are for *changing screen*. Three things in this product navigate without
 * doing that, and each would otherwise cross-fade the whole page: selecting a Ride, which only
 * fills the `split` outlet beside a list the Operator is still reading; `onSameUrlNavigation:
 * 'reload'`, which re-runs the URL already on screen; and a filter that writes its state to the
 * query string. Comparing this string before and after tells all three apart from a real
 * navigation.
 */
const primaryOutletPath = (root: ActivatedRouteSnapshot): string => {
  const segments: string[] = [];
  let route: ActivatedRouteSnapshot | undefined = root;
  while (route) {
    segments.push(route.url.map((segment) => segment.path).join('/'));
    route = route.children.find((child) => child.outlet === PRIMARY_OUTLET);
  }
  return segments.filter(Boolean).join('/');
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    /*
     * Overlays stack by z-index, not in the browser's top layer.
     *
     * CDK 21 opens every overlay — dialog, alert-dialog, select panel, date picker — by putting
     * its host in the top layer (`popover="manual"` + `showPopover()`). The top layer paints above
     * the whole document no matter what, so no z-index can reach over it: an error toast raised
     * while a ride action dialog was open rendered *behind* the dialog's backdrop, blurred and
     * unreadable, which is the one moment an Operator most needs to read it. The toaster cannot
     * answer that by bidding higher — sonner already asks for 999999999.
     *
     * Turning it off restores the pre-21 behaviour the rest of this app is built around: overlays
     * live in `.cdk-overlay-container` at z-index 1000, and `<hlm-toaster>` outranks them. Two
     * overlays still stack correctly against each other — a select opened inside a dialog is
     * appended to the same container later and paints above it.
     */
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    importProvidersFrom(ApiModule.forRoot(apiConfigFactory)),
    provideRouter(
      appRoutes,
      withInMemoryScrolling({ anchorScrolling: 'enabled', scrollPositionRestoration: 'enabled' }),
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
        paramsInheritanceStrategy: 'always',
      }),
      withExperimentalAutoCleanupInjectors(),
      /*
       * A cross-fade between screens, tuned to `--motion-fast` in `styles/spartan-theme.css`.
       * The browser does the work — there is no Angular animation package here and this adds no
       * bundle — and where `document.startViewTransition` is missing, including during SSR, it
       * is a no-op and navigation stays instant.
       *
       * `skipInitialTransition` because there is nothing to transition *from* on first paint;
       * without it the app fades in from a blank page on every hard load.
       */
      withViewTransitions({
        skipInitialTransition: true,
        onViewTransitionCreated: ({ transition, from, to }) => {
          if (primaryOutletPath(from) === primaryOutletPath(to)) {
            transition.skipTransition();
          }
        },
      }),
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideStore(
      {
        router: routerReducer,
        [authenticationFeatureKey]: authReducer,
        [AUTHORIZATION_FEATURE_KEY]: authorizationReducer,
        [OPERATION_AREAS_FEATURE_KEY]: operationAreasReducer,
        rideAlerts: provideRideAlertDomain().feature.reducer,
        ...CarDomainProviders.reducer,
      },
      { metaReducers },
    ),
    provideEffects([
      AuthenticationEffects,
      AuthorizationEffects,
      OperationAreasEffects,
      CarDomainProviders.effects,
      provideRideAlertDomain().effect,
    ]),
    provideRouterStore({
      serializer: CustomSerializer,
    }),

    ...provideTicketDomain(),

    // The OperatorHub connection, its state slice and its effects. Registered at the root because
    // every live screen — ride alerts, the open ride, the open ticket thread, report readiness,
    // support calls — reads from it (design §8.9).
    ...provideRealtimeDomain(),
    // With the flag off the media is `null`, which is what hides "Call driver" on driver details.
    {
      provide: SUPPORT_CALL_MEDIA,
      useFactory: () => (inject(SUPPORT_CALLS_ENABLED) ? inject(SupportCallsService) : null),
    },
    { provide: SUPPORT_CALLS_ENABLED, useValue: environment.supportCalls },

    provideClientHydration(withNoIncrementalHydration()),

    {
      provide: ErrorHandler,
      useClass: CustomErrorHandler,
    },

    provideHttpClient(withFetch(), withInterceptors([authInterceptor, loadingInterceptor])),
    // The interceptor and the calling store both report a failure; the Operator sees it once.
    provideProblemToastLifecycle(),
    !environment.production
      ? provideStoreDevtools({
          maxAge: 25,
          logOnly: !isDevMode(),
          name: 'NgRx Elite App',
          autoPause: true,
          trace: false,
          traceLimit: 75,
          connectInZone: true,
        })
      : [],
    { provide: APP_NAME, useValue: environment.appName },
    { provide: APP_LOGO_URL, useValue: environment.logoUrl },
    { provide: APP_VERSION, useValue: APP_VERSION_BUILD },
    { provide: TENANT_ID, useValue: environment.tenantId },
    // Initialize the update listener at app startup
    provideAppInitializer(() => {
      inject(AppUpdateService);
    }),
  ],
};
export function apiConfigFactory(): Configuration {
  const params: ConfigurationParameters = {
    basePath: environment.apiUrl,
  };
  return new Configuration(params);
}
